/// <amd-module name="VSS/Features/Charts/BubbleChartTabularComponent" />
import * as React from "react";
import { BubbleChartOptions, ChartProps, FreePointDatum, LabelFormatModes } from "VSS/Features/Charts/Contracts";
import { TabularComponent } from "VSS/Features/Charts/Internal/TabularComponent";
import * as Resources from "VSS/Features/Charts/Resources";

export function createBubbleChartTableModel(
    chartProps: ChartProps,
    seriesHeaderInterpreter: (chartProps: ChartProps, index: number) => string,
    xValueInterpreter: (labelFormatMode: LabelFormatModes, xValue: string) => string
): { headerData: string[]; tableValues: string[][] } {
    const headerData: string[] = [];
    const tableValues: string[][] = [];

    const hasXLabels = chartProps.xAxis && chartProps.xAxis.labelValues && chartProps.xAxis.labelValues.length > 0;
    const hasYLabels = chartProps.yAxis && chartProps.yAxis.labelValues && chartProps.yAxis.labelValues.length > 0;
    const hideSeriesColumn = chartProps.series && chartProps.series.length === 1 && !chartProps.series[0].name;

    !hideSeriesColumn && headerData.push(Resources.HtmlTable_SeriesHeaderName);
    headerData.push(chartProps.yAxis && chartProps.xAxis.title ? chartProps.xAxis.title : "x");
    headerData.push(chartProps.yAxis && chartProps.yAxis.title ? chartProps.yAxis.title : "y");
    headerData.push(((chartProps.specializedOptions || {}) as BubbleChartOptions).measureName || "z");

    let rowIndex = 0;
    for (let seriesIndex = 0, seriesLength = chartProps.series.length; seriesIndex < seriesLength; seriesIndex++) {
        for (const datum of chartProps.series[seriesIndex].data) {
            tableValues[rowIndex] = [];
            if (!hideSeriesColumn) {
                tableValues[rowIndex].push(seriesHeaderInterpreter(chartProps, seriesIndex));
            }

            const { x, y, z } = datum as FreePointDatum;

            // Populate "x"
            let xLabelValue: string = null;
            if (hasXLabels) {
                xLabelValue = chartProps.xAxis.labelValues[x];
                if (xLabelValue) {
                    xLabelValue = xValueInterpreter(chartProps.xAxis.labelFormatMode, xLabelValue);
                }
            }

            if (xLabelValue == null) {
                xLabelValue = Number(x).toLocaleString(navigator.language);
            }

            tableValues[rowIndex].push(xLabelValue || "");

            // Populate "y"
            let yLabelValue: string = null;
            if (hasYLabels) {
                yLabelValue = chartProps.yAxis.labelValues[y];
            }

            if (yLabelValue == null) {
                yLabelValue = Number(y).toLocaleString(navigator.language);
            }

            tableValues[rowIndex].push(yLabelValue || "");

            // Populate "z"
            const zLabelValue = Number(z).toLocaleString(navigator.language);
            tableValues[rowIndex].push(zLabelValue || "");

            rowIndex++;
        }
    }

    return { headerData, tableValues };
}

/**
 * Responsible for rendering a screen-readable variant of bubble chart content.
 * */
export class BubbleChartTabularComponent extends TabularComponent {
    public render(): JSX.Element {
        const chartProps = this.props;
        if (!chartProps.series || !this.props.showAccessibleForm) {
            return null;
        }

        const { headerData, tableValues } = createBubbleChartTableModel(
            chartProps,
            super.interpretSeriesHeader.bind(this),
            super.interpretXValue.bind(this)
        );
        const thElements = headerData.map((headerValue, index) => (
            <th scope="col" key={index}>
                {headerValue}
            </th>
        ));
        const bodyRows = tableValues.map((row, index) => {
            return (
                <tr key={index}>
                    {row.map((value, index) => (
                        <td scope="row" key={index}>
                            {value}
                        </td>
                    ))}
                </tr>
            );
        });

        return super.renderTable(thElements, bodyRows);
    }
}
